/* Monetisation model — policy defaults, body helpers and hot-tip placement
   geometry. Moved out of the old Tips page so the compact Monetisation page and
   the Content → Sales tab share one source of truth. Defaults are kept in sync
   with the backend (routes/tips.py). */
import type {
  HookStyle,
  HotTipPolicy,
  HotTipPolicyBody,
  NormalTipPolicy,
  NormalTipPolicyBody,
  PlacementRegime,
  PpvOfferPolicy,
  PpvOfferPolicyBody,
  SubOfferPolicy,
  SubOfferPolicyBody,
  VipOfferPolicy,
  VipOfferPolicyBody,
} from '../../../../api/client';

// Default hook libraries — kept in sync with the backend (routes/tips.py).
// Hot tips fire mid-sexting (in-heat); normal tips ride a long warm chat.
const DEFAULT_HOT_HOOK_STYLES: HookStyle[] = [
  {
    heading: 'Reward the moment',
    prompt:
      'Frame the tip as the reward that unlocks the peak of the moment — she gives more the second he treats her.',
  },
  {
    heading: 'Tease the next step',
    prompt:
      "Playfully gate the next escalation behind a small tip — 'show me you mean it and I'll keep going'.",
  },
  {
    heading: 'Flirty dare',
    prompt:
      "Turn it into a flirty dare — 'bet you won't' — daring him to spoil her right now while she's this worked up.",
  },
];

const DEFAULT_NORMAL_HOOK_STYLES: HookStyle[] = [
  {
    heading: 'Real gentleman',
    prompt:
      'Appeal to him being a real gentleman who likes taking care of her — a small tip is how a good guy shows it.',
  },
  {
    heading: 'Time together',
    prompt:
      "Gently note how much time and attention she's given him over the chat, and how nice it'd feel to be appreciated for it.",
  },
  {
    heading: 'Our connection',
    prompt:
      "Lean on the genuine connection and company they've built across the long conversation — the tip is a warm way to keep it going.",
  },
];

// VIP-offer hooks pitch the creator's separate paid VIP page (never a tip/PPV/price).
const DEFAULT_VIP_HOOK_STYLES: HookStyle[] = [
  {
    heading: 'Closer connection',
    prompt:
      'Lean on the bond they’ve built — the VIP page is where she actually gets to talk to him more, closer and more personal.',
  },
  {
    heading: 'More of her',
    prompt:
      'She shares so much more over there — more posts, more of her day, more of everything he already likes about her.',
  },
  {
    heading: 'Playful invite',
    prompt:
      "Light, flirty invite to come hang out with her on her VIP page — no pressure, just 'come find me over there'.",
  },
];

// Subscription-offer hooks pitch upgrading to the paid plan IN PLACE on the same
// profile (never a link, never a "VIP page"). Kept in sync with the backend
// (routes/tips.py DEFAULT_SUB_HOOK_STYLES).
const DEFAULT_SUB_HOOK_STYLES: HookStyle[] = [
  {
    heading: 'Closer connection',
    prompt:
      "Lean on the bond they've built — on her paid plan she actually gets to talk to him more, closer and more personal, right here on the same profile.",
  },
  {
    heading: 'More of her',
    prompt:
      'So much more opens up once he subscribes — more posts, more of her day, more of everything he already likes about her.',
  },
  {
    heading: 'Playful invite',
    prompt:
      "Light, flirty invite to upgrade and unlock her paid plan — no pressure, just 'come get closer to me, it's right here'.",
  },
];

export const HOT_DEFAULTS: HotTipPolicyBody = {
  is_active: false,
  hook_styles: DEFAULT_HOT_HOOK_STYLES,
  strategy_prompt: '',
  tip_min: 10,
  allow_more: true,
  min_turns_between_asks: 10,
  placement_regime: 'beginning_during',
  arc_span_turns: 30,
  // Start + during with 2 asks over 30 turns — presetPoints('beginning_during', 2, 30).
  ask_turn_points: [3, 15],
  ask_prices: null,
  ask_disclose_prices: null,
  max_negotiation_attempts: 2,
  negotiation_grace_turns: 2,
  max_negotiation_turns: 6,
  on_max_negotiations: 'skip',
  tip_drop_floor_pct: 0.5,
  negotiation_prompt: '',
  notify_on_decline: true,
  notify_on_completion: true,
};

export const NORMAL_DEFAULTS: NormalTipPolicyBody = {
  is_active: false,
  // Recommended starting trigger — low enough that a new user actually sees a tip
  // fire while testing; raise it if tips feel too frequent (see tutorial).
  chatting_trigger_turns: 20,
  hook_styles: DEFAULT_NORMAL_HOOK_STYLES,
  nudge_strategy: '',
  tip_min: 10,
  allow_more: true,
  disclose_amount: false,
  min_turns_between_asks: 30,
  chatting_cap: 1,
  overall_cap: 2,
  max_negotiation_attempts: 2,
  negotiation_grace_turns: 2,
  max_negotiation_turns: 6,
  on_max_negotiations: 'skip',
  tip_drop_floor_pct: 0.5,
  negotiation_prompt: '',
  propose_conditions: '',
  notify_on_decline: true,
  notify_on_completion: true,
};

export const VIP_DEFAULTS: VipOfferPolicyBody = {
  is_active: false,
  page_url: '',
  page_price: null,
  // Recommended starting trigger — low enough that a new user actually sees the VIP
  // offer fire while testing; raise it if it feels too frequent (see tutorial).
  trigger_turns: 20,
  hook_styles: DEFAULT_VIP_HOOK_STYLES,
  offer_strategy: '',
  negotiation_prompt: '',
  propose_conditions: '',
  max_offers_per_session: 1,
  max_offers_lifetime: 1,
  min_turns_between_asks: 50,
  combined_session_cap: 1,
  min_turns_since_last_tip: 50,
  negotiation_grace_turns: 0,
  max_negotiation_attempts: 2,
  max_negotiation_turns: 6,
  on_max_negotiations: 'ease',
  block_after_decline: true,
  notify_on_decline: true,
  notify_on_completion: true,
};

// Base offer brief seeded into a fresh policy (kept in sync with the backend
// DEFAULT_SUB_OFFER_BRIEF) so operators start from a usable, on-profile pitch.
export const DEFAULT_SUB_OFFER_BRIEF =
  "Invite him to upgrade to my paid plan — it's right here on this same profile, no " +
  "link and no other page to go to. Keep it warm and personal: on the paid plan he " +
  "gets closer to me, all my full posts and everyday updates, and we can keep talking " +
  "like this. Work the price and what he gets into the conversation naturally in your " +
  "own words (edit this with your actual price and perks). Never pushy — a genuine " +
  "invite, not a sales pitch.";

export const SUB_DEFAULTS: SubOfferPolicyBody = {
  is_active: false,
  offer_brief: DEFAULT_SUB_OFFER_BRIEF,
  instruction_content_ids: [],
  // Recommended starting trigger — low enough that a new user actually sees the
  // paid-plan offer fire while testing; raise it if it feels too frequent.
  trigger_turns: 15,
  hook_styles: DEFAULT_SUB_HOOK_STYLES,
  negotiation_prompt: '',
  propose_conditions: '',
  max_offers_per_session: 1,
  max_offers_lifetime: 1,
  min_turns_between_asks: 50,
  combined_session_cap: 1,
  min_turns_since_last_tip: 50,
  negotiation_grace_turns: 0,
  max_negotiation_attempts: 2,
  max_negotiation_turns: 6,
  on_max_negotiations: 'ease',
  block_after_decline: true,
  // Alerts default OFF — the offer runs unattended unless an operator opts in.
  notify_on_decline: false,
  notify_on_completion: false,
};


// PPV-offer hook libraries — kept in sync with the backend (routes/tips.py).
// Two moments: earn the FIRST unlock (sell) vs ride a fresh buyer (upsell).
const DEFAULT_PPV_SELL_HOOK_STYLES: HookStyle[] = [
  {
    heading: 'Made it thinking of you',
    prompt:
      'Quiet and personal — she made this and he’s who came to mind. The offer is the confession, not a sale. Soft, a little shy about admitting it.',
  },
  {
    heading: 'Let him in',
    prompt:
      'Frame it as letting him see something private she doesn’t show around — intimate and trusting rather than loud or salesy.',
  },
  {
    heading: 'Playful dare',
    prompt:
      'Cocky and certain he wants it — ‘bet you won’t’, ‘i dare you’. Teasing and confident, never needy and never actually rude to him.',
  },
];
const DEFAULT_PPV_UPSELL_HOOK_STYLES: HookStyle[] = [
  {
    heading: 'One step further',
    prompt:
      'He just took the first one, so the follow-on goes further than it did — same her, less held back. Say plainly this one is the bolder one.',
  },
  {
    heading: 'Pleased with him',
    prompt:
      'She’s visibly delighted he took it and that good mood is what produces the second offer — warm, a little smug, riding the moment together.',
  },
];

export const PPV_DEFAULTS: PpvOfferPolicyBody = {
  is_active: false,
  pricing_rules: null,
  use_content_request_pricing: true,
  propose_conditions: '',
  propose_aggression: 'balanced',
  theme_guidance: '',
  offer_strategy: '',
  sell_hook_styles: DEFAULT_PPV_SELL_HOOK_STYLES,
  upsell_hook_styles: DEFAULT_PPV_UPSELL_HOOK_STYLES,
  tease_before_send: true,
  allow_upsell: true,
  max_upsell_chain: null,
  handoff_on_agreement: false,
  max_offers_per_session: 2,
  min_turns_between_offers: 20,
  cooldown_turns_after_decline: 40,
  min_session_turns: 10,
  lifetime_cap: null,
  negotiation_grace_turns: 1,
  max_negotiation_attempts: 3,
  max_negotiation_turns: 6,
  on_max_negotiations: 'ease',
};

/** Strip the response-only `exists`/`id` fields, leaving a pure policy body. */
function stripMeta<P extends { exists?: boolean; id?: number | null }, B>(p: P): B {
  const r = { ...p } as Partial<P>;
  delete r.exists;
  delete r.id;
  return r as unknown as B;
}
/** A loaded hot-tip policy always carries its resolved ask points (ADR 0065): a row saved
 *  before the console persisted preset points is filled here, so the next save writes them. */
export const hotBodyOf = (p: HotTipPolicy) => {
  const b = stripMeta<HotTipPolicy, HotTipPolicyBody>(p);
  const turns = resolveAsks(b).map((a) => a.turn);
  return { ...b, ask_turn_points: turns.length ? turns : null };
};
export const normalBodyOf = (p: NormalTipPolicy) => stripMeta<NormalTipPolicy, NormalTipPolicyBody>(p);
export const vipBodyOf = (p: VipOfferPolicy) => stripMeta<VipOfferPolicy, VipOfferPolicyBody>(p);
export const subBodyOf = (p: SubOfferPolicy) => stripMeta<SubOfferPolicy, SubOfferPolicyBody>(p);
export const ppvBodyOf = (p: PpvOfferPolicy) => stripMeta<PpvOfferPolicy, PpvOfferPolicyBody>(p);

// ── Hot-tip Ask schedule ─────────────────────────────────────────────
// ADR 0065 (bigdayz.new). The schedule is a list of ASK POINTS in arc turns — the hot hook
// is arc turn 1, so a point is 2 or later. A placement regime is only a preset that fills
// the points, and the resolved points are SAVED for every regime: the brain reads them and
// never re-derives them, so what this preview shows is exactly what runs.
//
// presetPoints / normalizePoints MUST stay identical to bigdayz.new
// response_generation_with_graph/hot_tip_schedule.py (preset_points / _normalize) and the
// 20260929180000 backfill migration. Both repos pin the same fixture table in tests.

export const MAX_ASKS = 6;
/** The hot hook owns arc turn 1; the first ask can land on arc turn 2 at the earliest. */
export const MIN_ASK_TURN = 2;
/** Bars in the compact placement preview; each covers 1/12 of the arc. */
export const PREVIEW_BARS = 12;
/** Asks a count-driven preset starts from when there is nothing to carry over. */
const DEFAULT_PRESET_ASKS = 2;

/** Presets whose ask count the operator picks (Repeated, Start + during, During + end);
 *  the other presets have a fixed shape. */
export const isCountPreset = (r: PlacementRegime) => r === 'during' || r === 'beginning_during' || r === 'during_end';

const FIXED_PRESET_ASKS: Partial<Record<PlacementRegime, number>> = { beginning_only: 1, beginning_end: 2, beginning_middle_end: 3 };

/** How many asks a preset yields for a row that has no saved points (saved before ADR 0065):
 *  the retired hot cap clamped by the retired overall cap, then by the preset's shape. */
function legacyPresetCount(regime: PlacementRegime, hotCap: number | undefined, overallCap: number | undefined): number {
  const cap = Math.max(1, Math.min(hotCap || 2, overallCap || 2));
  return Math.min(FIXED_PRESET_ASKS[regime] ?? cap, cap);
}

/** Where a preset's n asks sit on the 0..1 arc. */
export function anchorsForRegime(regime: PlacementRegime, n: number): number[] {
  const mid = (k: number, m: number) => (m <= 1 ? 0.5 : 0.34 + (0.66 - 0.34) * (k / (m - 1)));
  const spreadMid = (m: number) => Array.from({ length: Math.max(0, m) }, (_, k) => mid(k, m));
  if (n <= 0) return [];
  switch (regime) {
    case 'beginning_only':
      return [0.1];
    case 'during':
      return spreadMid(n);
    case 'beginning_during':
      return [0.1, ...spreadMid(n - 1)];
    case 'beginning_end':
      return n >= 2 ? [0.1, 0.92] : [0.1];
    case 'during_end':
      return n >= 2 ? [...spreadMid(n - 1), 0.92] : [0.92];
    case 'beginning_middle_end':
      return n >= 3 ? [0.1, 0.5, 0.92] : n === 2 ? [0.1, 0.92] : [0.1];
    default:
      return spreadMid(n);
  }
}

/** Sorted, clamped to [2, 999], strictly increasing (a collision moves one turn later so
 *  two asks never share a turn), at most MAX_ASKS. */
export function normalizePoints(points: number[]): number[] {
  const out: number[] = [];
  for (const raw of [...points].sort((a, b) => a - b)) {
    const prev = out[out.length - 1];
    let p = Math.max(MIN_ASK_TURN, Math.min(999, Math.round(raw)));
    if (prev !== undefined && p <= prev) p = prev + 1;
    out.push(p);
  }
  return out.slice(0, MAX_ASKS);
}

/** A preset's ask points for n asks over an arc of `span` turns. */
export function presetPoints(regime: PlacementRegime, n: number, span: number): number[] {
  const s = span || 30;
  return normalizePoints(anchorsForRegime(regime, n).map((pos) => Math.round(pos * s)));
}

export type ArcPhase = 'early' | 'mid' | 'peak';
export const phaseOf = (pos: number): ArcPhase => (pos < 0.28 ? 'early' : pos < 0.74 ? 'mid' : 'peak');

/** One ask as it will run. Every point fires — the placements alone decide the count. */
export interface HotAsk { turn: number; pos: number; price: number | null; say: boolean }

/** The turn scale of the preview: the arc span, stretched to fit the points. */
export function axisMax(p: HotTipPolicyBody): number {
  return Math.max(p.arc_span_turns || 30, ...(p.ask_turn_points ?? []), 1);
}

/** The saved schedule's points, or — for a preset row saved before ADR 0065 — the
 *  preset's points. */
function pointsOf(p: HotTipPolicyBody): number[] {
  if (p.ask_turn_points && p.ask_turn_points.length) return normalizePoints(p.ask_turn_points);
  const regime = p.placement_regime || 'beginning_during';
  if (regime === 'custom') return [];
  return presetPoints(regime, legacyPresetCount(regime, p.hot_tip_cap, p.overall_cap), p.arc_span_turns);
}

/** The asks exactly as the engine runs them, in ordinal order. */
export function resolveAsks(p: HotTipPolicyBody): HotAsk[] {
  const priceAt = (i: number) => (p.ask_prices && i < p.ask_prices.length ? p.ask_prices[i] ?? null : null);
  const sayAt = (i: number) => !!(p.ask_disclose_prices && i < p.ask_disclose_prices.length && p.ask_disclose_prices[i]);
  const pts = pointsOf(p);
  const ax = Math.max(p.arc_span_turns || 30, ...pts, 1);
  return pts.map((turn, i) => ({ turn, pos: Math.max(0, Math.min(1, turn / ax)), price: priceAt(i), say: sayAt(i) }));
}

type AskEdit = Pick<HotAsk, 'turn' | 'price' | 'say'>;

/** Decompose asks back into the three ordinal-indexed arrays. The points are saved for
 *  every regime; price/say arrays collapse to null when nothing is set. */
export function writeAsks(asks: AskEdit[], regime: PlacementRegime): Partial<HotTipPolicyBody> {
  const a = asks.slice().sort((x, y) => x.turn - y.turn);
  const tp = normalizePoints(a.map((x) => x.turn));
  const pr = a.map((x) => x.price);
  const ds = a.map((x) => x.say);
  return {
    placement_regime: regime,
    ask_turn_points: tp.length ? tp : null,
    ask_prices: pr.every((x) => x == null) ? null : pr,
    ask_disclose_prices: ds.every((x) => !x) ? null : ds,
  } as Partial<HotTipPolicyBody>;
}

/** Re-fill a preset's points (n asks over `span`), keeping each ordinal's price/say. */
function fillPreset(p: HotTipPolicyBody, regime: PlacementRegime, n: number, span: number): Partial<HotTipPolicyBody> {
  const cur = resolveAsks(p);
  const asks = presetPoints(regime, n, span).map((turn, i) => ({ turn, price: cur[i]?.price ?? null, say: cur[i]?.say ?? false }));
  return writeAsks(asks, regime);
}

/** Switch placement regime. A preset fills the points; custom starts from whatever the
 *  schedule was, so switching to it never moves an ask. */
export function setRegime(p: HotTipPolicyBody, r: PlacementRegime): Partial<HotTipPolicyBody> {
  if (r === 'custom') return { placement_regime: r };
  const n = FIXED_PRESET_ASKS[r] ?? (resolveAsks(p).length || DEFAULT_PRESET_ASKS);
  return fillPreset(p, r, n, p.arc_span_turns);
}

/** Count presets: change how many asks the preset spreads. */
export function setAskCount(p: HotTipPolicyBody, n: number): Partial<HotTipPolicyBody> {
  const r = p.placement_regime || 'beginning_during';
  return fillPreset(p, r, Math.max(1, Math.min(MAX_ASKS, n)), p.arc_span_turns);
}

/** Arc length: presets anchor to it, so their points move with it; custom points stay. */
export function setArcSpan(p: HotTipPolicyBody, span: number): Partial<HotTipPolicyBody> {
  const r = p.placement_regime || 'beginning_during';
  if (r === 'custom') return { arc_span_turns: span };
  return { arc_span_turns: span, ...fillPreset(p, r, resolveAsks(p).length || DEFAULT_PRESET_ASKS, span) };
}

/** The turn a preview bar stands for: its centre on the axis, so an ask added
 *  there resolves back into the same bar. */
export const barTurn = (k: number, ax: number) => Math.max(MIN_ASK_TURN, Math.min(999, Math.round(((k + 0.5) / PREVIEW_BARS) * ax)));
/** Which preview bar an arc position falls in. */
export const barOf = (pos: number) => Math.min(PREVIEW_BARS - 1, Math.max(0, Math.floor(pos * PREVIEW_BARS)));

/** Custom placement: clicking bar k adds an ask there, or removes the asks in it. */
export function toggleBar(p: HotTipPolicyBody, k: number): Partial<HotTipPolicyBody> {
  const ax = axisMax(p);
  const cur = resolveAsks(p);
  const inBar = cur.filter((a) => barOf(a.pos) === k);
  if (inBar.length) return writeAsks(cur.filter((a) => barOf(a.pos) !== k), 'custom');
  if (cur.length >= MAX_ASKS) return {};
  return writeAsks([...cur, { turn: barTurn(k, ax), price: null, say: false }], 'custom');
}
